// src/resources/Letters/MDLetterUtils.js
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * 🔹 Fetch letter and company
 */
export const fetchLetterAndCompany = async (
  id,
  setLetter,
  setCompany,
  setLoading
) => {
  try {
    setLoading(true);
    const letterSnap = await getDoc(doc(db, "letters", id));
    if (!letterSnap.exists()) {
      console.warn("⚠️ Letter not found:", id);
      setLetter(null);
      setLoading(false);
      return;
    }

    const data = letterSnap.data();
    setLetter({ id: letterSnap.id, ...data });

    if (data.companyId) {
      const companySnap = await getDoc(doc(db, "companies", data.companyId));
      if (companySnap.exists()) setCompany(companySnap.data());
    }

    setLoading(false);
  } catch (err) {
    console.error("❌ Error loading letter:", err);
    setLoading(false);
  }
};

/**
 * 🔏 Create signed PDF with correct rendering (no black screen)
 *  → Logic is same as before (upload, Firestore update, redirect)
 *  → Only the html2canvas capture has been aligned with pdfHelper.js
 */
export const handleSignAndFinalize = async ({
  letterNode,
  letter,
  id,
  setDone,
  navigate,
}) => {
  try {
    if (!letterNode) {
      alert("⚠️ Letter preview not ready for export.");
      return;
    }

    const mdEmail = localStorage.getItem("email") || "";

    // ----------------------------------------------------
    // 1) CLONE THE NODE (safe snapshot, off-screen)
    // ----------------------------------------------------
    const clone = letterNode.cloneNode(true);
    clone.style.position = "absolute";
    clone.style.left = "-9999px";
    clone.style.top = "0";
    clone.style.zIndex = "-1";
    clone.style.background = "#ffffff";
    clone.style.overflow = "visible";
    clone.style.width = `${letterNode.offsetWidth}px`;
    clone.style.height = `${letterNode.offsetHeight}px`;

    document.body.appendChild(clone);

    // ----------------------------------------------------
    // 2) ENSURE ALL IMAGES ARE CORS-SAFE & FULLY LOADED
    //    (letterhead, signature stamp, etc.)
    // ----------------------------------------------------
    const imgs = clone.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise((resolve) => {
            try {
              img.crossOrigin = "anonymous";
              img.referrerPolicy = "no-referrer";
            } catch (e) {
              // ignore if browser blocks setting these
            }
            if (img.complete) resolve();
            else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          })
      )
    );

    // ----------------------------------------------------
    // 3) NORMALIZE STYLES (direction, fonts, background)
    // ----------------------------------------------------
    const isRTL = (letter.direction || "ltr").toLowerCase() === "rtl";
    clone.style.direction = isRTL ? "rtl" : "ltr";
    clone.style.fontFamily = isRTL
      ? "'Amiri','Noto Naskh Arabic',serif"
      : "Calibri, Arial, sans-serif";
    clone.style.color = "#000";
    clone.style.textAlign = isRTL ? "right" : "justify";
    clone.style.backgroundColor = "#ffffff";

    // Quill alignment helpers (same idea as pdfHelper)
    clone.querySelectorAll(".ql-align-center").forEach((el) => {
      el.style.textAlign = "center";
    });
    clone.querySelectorAll(".ql-align-right").forEach((el) => {
      el.style.textAlign = "right";
    });
    clone.querySelectorAll(".ql-align-justify").forEach((el) => {
      el.style.textAlign = "justify";
    });

    // Small delay to let browser layout the clone
    await new Promise((res) => setTimeout(res, 300));

    // ----------------------------------------------------
    // 4) CAPTURE WITH html2canvas (aligned with pdfHelper.js)
    // ----------------------------------------------------
    const canvas = await html2canvas(clone, {
      scale: 4, // high resolution
      useCORS: true,
      allowTaint: true, // match your working pdfHelper config
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: clone.scrollWidth,
    });

    // Clean up the clone
    document.body.removeChild(clone);

    // ----------------------------------------------------
    // 5) CREATE PDF FROM CANVAS
    // ----------------------------------------------------
    const imgData = canvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * pageWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, height);

    // ----------------------------------------------------
    // 6) UPLOAD SIGNED PDF TO FIREBASE STORAGE
    // ----------------------------------------------------
    const safeRef = letter.referenceNo
      ? letter.referenceNo.replace(/[/\\\s]+/g, "_")
      : `Letter_${id}`;
    const fileName = `${safeRef}.pdf`;
    const storageRef = ref(storage, `letters/signed/${fileName}`);
    const blob = pdf.output("blob");
    await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(storageRef);

    // ----------------------------------------------------
    // 7) UPDATE FIRESTORE LETTER DOCUMENT
    // ----------------------------------------------------
    await updateDoc(doc(db, "letters", id), {
      status: "signed",
      signedBy: mdEmail,
      signedAt: serverTimestamp(),
      signedPdfUrl: downloadURL,
    });

    console.log("✅ Uploaded signed letter:", fileName);
    setDone(true);
    setTimeout(() => navigate("/signedLetters"), 1500);
  } catch (err) {
    console.error("❌ Error signing letter:", err);
    alert("Failed to sign the letter: " + err.message);
  }
};
